import {
  Button,
  EButtonColorVariant,
  EHelpTextVariant,
  ELinkButtonVariant,
  ETextInputTypes,
  EToastVariant,
  GeneralSocialColoredAppleIcon,
  GeneralSocialColoredFacebookIcon,
  GeneralSocialColoredGoogleIcon,
  GeneralXCircleIcon,
  HelpText,
  LinkButton,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FORGOT_PAGE } from '@/constants/urls';
import { emailValidator, passwordValidator } from '@/constants/validationRules';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { useToast } from '@/hooks/useToast';
import { clearAuthError, setJustLoggedIn, TAuthError } from '@/redux/modules/auth';
import { IUserResponse } from '@/redux/modules/auth/userMini';
import apiRequest from '@/services/apiRequest';
import { ILoginForm, TAccountType } from '@/services/types/auth/ILoginForm';
import { ILoginResponse } from '@/services/types/auth/ILoginResponse';
import { getCoreApi } from '@/utility/getCoreApi';
import { initiateSocialAuth } from '@/utility/initiateSocialAuth';
import { setSessionCookie } from '@/utility/session.login';
import { triggerCustomEvent } from '@/utility/triggerCustomEvent';

import { getAuthErrorMessage } from './getAuthErrorMessage';
import { TFieldErrors } from './types/TFieldErrors';

// Provider metadata for the "Continue with {provider}" prompt shown when a password
// is entered for a social-login account.
const SOCIAL_META: Partial<Record<TAccountType, { label: string; icon: JSX.Element }>> = {
  google: { label: 'Google', icon: <GeneralSocialColoredGoogleIcon fontSize={20} /> },
  facebook: { label: 'Facebook', icon: <GeneralSocialColoredFacebookIcon fontSize={20} /> },
  apple: { label: 'Apple', icon: <GeneralSocialColoredAppleIcon fontSize={20} /> },
};

export interface IProps {
  isModal?: boolean;
  onLogin: (credentials: ILoginForm) => void;
  isLoading?: boolean;
  error?: TAuthError | null;
  onForgotPassword?: () => void;
  email?: string;
  reCaptchaToken: string;
}

const LoginForm: React.FC<IProps> = ({ onLogin, reCaptchaToken, ...props }) => {
  const recaptchaRef: MutableRefObject<ReCAPTCHA | null> = useRef(null);
  const shouldUseRecaptcha = !!reCaptchaToken;
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const { locale } = useLocaleSettings();
  const [countdown, setCountdown] = useState<number>(0);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  //#region INTL
  const intl = useIntl();
  const email = intl.formatMessage({ defaultMessage: 'Email address', id: 'hJZwTS' });
  const password = intl.formatMessage({ defaultMessage: 'Password', id: '5sg7KC' });
  const btnLabelLogin = intl.formatMessage({ defaultMessage: 'Log in', id: 'odXlk8' });
  const linkLabelForgot = intl.formatMessage({
    defaultMessage: 'Forgot your password?',
    id: 'cyRU1N',
  });
  const labelLoginFormHeader = intl.formatMessage({
    defaultMessage: 'Log in with your email',
    id: 's/9dk9',
    description: 'Login Modal Title',
  });
  const showPasswordLabel = intl.formatMessage({
    defaultMessage: 'Show password',
    id: 'JHTwdB',
  });
  const emailNotVerifiedError = intl.formatMessage({
    id: '99xPFB',
    defaultMessage: 'Please verify your email address before logging in.',
    description: 'Login Form > Error message when email is not verified',
  });
  //#endregion

  // Start each visit to the form with a clean slate (no stale error from a prior attempt).
  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  //#region Form Validation & Submit
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ILoginForm>();

  const onSubmit = useCallback(
    async (formValues: ILoginForm) => {
      const token = (shouldUseRecaptcha && (await recaptchaRef.current?.executeAsync())) || '';
      const data = { ...formValues, recaptcha_response: token };
      await onLogin(data);
      recaptchaRef.current?.reset();
    },
    [onLogin, shouldUseRecaptcha],
  );

  const fieldErrors: TFieldErrors<keyof ILoginForm> = {
    email: {
      required: intl.formatMessage({
        defaultMessage: 'Email is required',
        id: 'cU/aSG',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Please enter a valid email address.',
        id: 'xs/MJu',
      }),
    },
    password: {
      required: intl.formatMessage({
        defaultMessage: 'Password is required.',
        id: 'pRvgsc',
      }),
    },
  };

  const emailValue = watch('email');
  const passwordValue = watch('password');
  const fieldsAreEmpty = !emailValue || !passwordValue;

  const emailErr = errors.email && fieldErrors.email[errors.email.type];
  const passErr = errors.password && fieldErrors.password[errors.password.type];

  // Format error message, using internationalized message for email verification error
  const getErrorMessage = () => {
    if (!props.error) return '';

    const errorObj = typeof props.error === 'object' ? props.error : null;

    // Check if this is the email verification error using errorType
    if (errorObj?.errorType === 'EMAIL_NOT_VERIFIED') {
      return emailNotVerifiedError;
    }

    // Map the backend error code onto friendly, actionable copy.
    return getAuthErrorMessage(intl, props.error)?.message ?? '';
  };

  const isEmailNotVerifiedError =
    props.error &&
    typeof props.error === 'object' &&
    props.error.errorType === 'EMAIL_NOT_VERIFIED';

  // Rich display info (e.g. a social-login account). Falls back to a plain message
  // when the backend only sends the generic code, so it works before the api-go
  // provider-specific codes ship.
  const errorDisplay =
    props.error && !isEmailNotVerifiedError ? getAuthErrorMessage(intl, props.error) : null;
  const social = errorDisplay?.socialProvider
    ? SOCIAL_META[errorDisplay.socialProvider]
    : undefined;

  const resendLinkText = intl.formatMessage({
    id: 'bF1UsS',
    defaultMessage: 'Click here',
    description: 'Login Form > Resend verification email link',
  });

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Get the verification token from the error
  const verificationToken =
    props.error && typeof props.error === 'object' ? props.error.token : undefined;

  // Polling effect for email verification
  useEffect(() => {
    if (!isEmailNotVerifiedError || !verificationToken) return;

    let pollCount = 0;
    const maxPolls = 120; // 120 polls * 5 seconds = 10 minutes
    let isCancelled = false;

    const pollVerification = async () => {
      try {
        const user = await apiRequest<IUserResponse>(
          {
            url: `${getCoreApi()}/users/mini_me`,
            method: 'GET',
          },
          true,
          false,
          verificationToken,
        );

        // Check cancellation flag after async operation completes
        if (isCancelled) return;

        if (user?.verifications?.email === true) {
          // Email is verified! Rebuild login response and complete the login
          const loginResponse: ILoginResponse = {
            token: verificationToken,
            user_id: user.id,
            show_verify_email_screen: false,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone,
          };

          dispatch(setJustLoggedIn());
          await setSessionCookie(loginResponse, dispatch);
          triggerCustomEvent('session', 'load-user');

          // Show success toast for background verification
          showToast(
            EToastVariant.Success,
            intl.formatMessage({
              defaultMessage: 'Success',
              id: 'xrKHS6',
            }),
            intl.formatMessage({
              id: 'mP+3YW',
              defaultMessage:
                'Your email has been verified and you have been successfully logged in.',
              description:
                'Login Form > Success message when email verification completes in background',
            }),
          );

          return; // Stop polling
        }

        pollCount++;
        if (pollCount < maxPolls && !isCancelled) {
          pollingTimeoutRef.current = setTimeout(pollVerification, 5000);
        }
      } catch {
        // Check cancellation flag before continuing
        if (isCancelled) return;

        // Continue polling even on error
        pollCount++;
        if (pollCount < maxPolls && !isCancelled) {
          pollingTimeoutRef.current = setTimeout(pollVerification, 5000);
        }
      }
    };

    // Start polling
    pollVerification();

    // Cleanup on unmount
    return () => {
      isCancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [isEmailNotVerifiedError, verificationToken, dispatch, intl, showToast]);

  const sendLink = async () => {
    // Get the token from the error object
    const token = props.error && typeof props.error === 'object' ? props.error.token : undefined;

    if (!token) {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          id: 'g6ITzH',
          defaultMessage: 'Unable to resend verification email. Please try logging in again.',
        }),
      );
      return;
    }

    try {
      // Call the API with the token
      await apiRequest(
        {
          url: `${getCoreApi()}/auth/creds/resend`,
          method: 'POST',
        },
        true,
        false,
        token,
      );

      setCountdown(60); // Start 60 second countdown
      showToast(
        EToastVariant.Success,
        intl.formatMessage({
          defaultMessage: 'Success',
          id: 'xrKHS6',
        }),
        intl.formatMessage({
          defaultMessage: "We've sent a verification email.",
          id: '+i/ZIw',
        }),
      );
    } catch {
      showToast(
        EToastVariant.Error,
        intl.formatMessage({
          defaultMessage: 'Error',
          id: 'KN7zKn',
        }),
        intl.formatMessage({
          defaultMessage: 'Oops, something went wrong. Please, try again later.',
          id: 'uSQB3M',
        }),
      );
    }
  };

  // When the account uses social login, replace the email/password form with a focused
  // "Continue with {provider}" prompt. "Try another way" clears the error, which brings
  // the form (and the surrounding footer) back.
  if (!props.isLoading && social && errorDisplay?.socialProvider) {
    return (
      <div className="flex flex-col gap-3" role="alert">
        <div className="flex items-center justify-center text-sm gap-2">
          {social.icon}
          <span>{errorDisplay.message}</span>
        </div>
        <Button
          className="w-full"
          variant={EButtonColorVariant.Secondary}
          onClick={() => initiateSocialAuth(errorDisplay.socialProvider as TAccountType, locale)}
          label={
            <div className="flex items-center gap-2">
              {social.icon}
              {intl.formatMessage(
                {
                  id: 'wdnYn1',
                  defaultMessage: 'Continue with {provider}',
                  description: 'Login Form > Continue with social provider CTA',
                },
                { provider: social.label },
              )}
            </div>
          }
        />
        <div className="mx-auto">
          <LinkButton
            variant={ELinkButtonVariant.Underline}
            onClick={() => dispatch(clearAuthError())}
            label={intl.formatMessage({
              id: '3cjW5z',
              defaultMessage: 'Try another way',
              description: 'Login Form > Dismiss social-login prompt and return to the form',
            })}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      {!props.isLoading && props.error && !isEmailNotVerifiedError && (
        <HelpText
          className="mb-2"
          text={getErrorMessage()}
          variant={EHelpTextVariant.Error}
          showIcon
        />
      )}
      {!props.isLoading && isEmailNotVerifiedError && (
        <div className="flex items-start mb-2 gap-2">
          <div className="flex-shrink-0 text-sm">
            <GeneralXCircleIcon className="text-red-600" />
          </div>
          <div className="text-sm text-red-600">
            {emailNotVerifiedError}{' '}
            {countdown > 0 ? (
              <span>
                {intl.formatMessage(
                  {
                    id: 'MK11Wv',
                    defaultMessage: 'Please wait {seconds} seconds before retrying.',
                  },
                  { seconds: countdown },
                )}
              </span>
            ) : (
              <>
                <a
                  href="#"
                  className="font-bold text-red-600 hover:underline hover:text-red-700"
                  onClick={e => {
                    e.preventDefault();
                    sendLink();
                  }}>
                  {resendLinkText}
                </a>
                {intl.formatMessage({
                  id: 'VM6WaT',
                  defaultMessage: ' to resend the verification email.',
                })}
              </>
            )}
          </div>
        </div>
      )}
      {!props.isModal && <div className="mb-2">{labelLoginFormHeader}</div>}
      <form className="flex flex-col w-full pt-2 pb-2 bg-white" onSubmit={handleSubmit(onSubmit)}>
        <div className={emailErr ? 'mb-8' : 'mb-4'}>
          <Controller
            name="email"
            control={control}
            rules={emailValidator}
            defaultValue={props.email}
            render={({ field }) => (
              <TextInput
                controlled
                name="email"
                autoComplete="email"
                label={email}
                ariaLabel={email}
                inputValue={field.value || ''}
                onChange={field.onChange}
                error={!!emailErr}
                helpText={emailErr}
              />
            )}
          />
        </div>

        <div className={passErr ? 'mb-8' : 'mb-4'}>
          <Controller
            name="password"
            control={control}
            rules={passwordValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="password"
                autoComplete="current-password"
                label={password}
                ariaLabel={password}
                inputValue={field.value || ''}
                onChange={field.onChange}
                error={!!passErr}
                helpText={passErr}
                type={ETextInputTypes.Password}
                showPasswordButtonAriaLabel={showPasswordLabel}
              />
            )}
          />
        </div>

        <div className="flex items-center mt-6 space-y-0 space-x-6">
          <Button
            className="w-full flex-2"
            variant={EButtonColorVariant.Primary}
            label={btnLabelLogin}
            loading={props.isLoading}
            disabled={props.isLoading || fieldsAreEmpty}
            type="submit"
          />
          {props.isModal ? (
            <LinkButton
              className="justify-center flex-1 w-full"
              variant={ELinkButtonVariant.Underline}
              label={linkLabelForgot}
              onClick={() => {
                if (props.onForgotPassword) {
                  props.onForgotPassword();
                }
              }}
            />
          ) : (
            <LinkButton
              className="justify-center flex-1 w-full"
              variant={ELinkButtonVariant.Underline}
              label={linkLabelForgot}
              href={FORGOT_PAGE}
            />
          )}
        </div>
        {shouldUseRecaptcha && (
          <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={reCaptchaToken} />
        )}
      </form>
    </>
  );
};

export { LoginForm };
